import type { Message } from 'ai';
import { createScopedLogger } from '~/utils/logger';
import type { ChatHistoryItem } from './useChatHistory';
import type { StoreFileAction } from '~/types/actions';
import * as Sentry from '@sentry/remix';
import { userStore } from '~/lib/stores/user';

const logger = createScopedLogger('ChatHistory');

// Check if boltHistory database exists
export async function checkBoltHistoryExists(): Promise<boolean> {
  return new Promise((resolve) => {
    const request = indexedDB.open('boltHistory');

    request.onsuccess = () => {
      const db = request.result;

      try {
        if (db?.objectStoreNames?.length === 0) {
          console.log('[checkBoltHistoryExists] No object stores found in boltHistory database');

          /*
           * alert(
           *   "Old IndexDB existed but empty. You're now using the latest version of Dualite Alpha. No need to worry, a routine upgrade was done. \n\n Enjoy your day!",
           * );
           */
          db.close();
          resolve(false);
        }
      } catch (error) {
        /*
         * Sentry.captureException(error, {
         *   extra: {
         *     message: 'Error in db migration',
         *   },
         * });
         */
        console.log('db migration edge case failed. contact support');
      }

      console.log('[checkBoltHistoryExists] db:', db);
      db.close();
      resolve(true);
    };

    request.onerror = () => {
      resolve(false);
    };
  });
}

// Open boltHistory database
export async function openBoltDatabase(): Promise<IDBDatabase | undefined> {
  return new Promise((resolve) => {
    const request = indexedDB.open('boltHistory', 1);

    request.onsuccess = (event: Event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event: Event) => {
      resolve(undefined);
      logger.error((event.target as IDBOpenDBRequest).error);
    };
  });
}

// Delete boltHistory database after migration
export async function deleteBoltHistory(): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase('boltHistory');

    request.onsuccess = () => {
      logger.info('boltHistory database deleted successfully');
      resolve();
    };

    request.onerror = () => {
      logger.error('Failed to delete boltHistory database');
      reject(new Error('Failed to delete boltHistory database'));
    };
  });
}

// this is used at the top level and never rejects
export async function openDatabase(): Promise<IDBDatabase | undefined> {
  return new Promise((resolve) => {
    const request = indexedDB.open('dualiteHistory', 1);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains('chats')) {
        const store = db.createObjectStore('chats', { keyPath: 'id' });
        store.createIndex('id', 'id', { unique: true });
        store.createIndex('urlId', 'urlId', { unique: true });
      }
    };

    request.onsuccess = (event: Event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event: Event) => {
      resolve(undefined);
      logger.error((event.target as IDBOpenDBRequest).error);
    };
  });
}

export async function getAll(db: IDBDatabase): Promise<ChatHistoryItem[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result as ChatHistoryItem[]);
    request.onerror = () => reject(request.error);
  });
}

// = { language: 'ts', framework: 'react' }

export async function setMessages(
  db: IDBDatabase,
  id: string,
  messages: Message[],
  fileActions: StoreFileAction[],
  framework: string,
  language: string,
  urlId?: string,
  description?: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');
    const techStack = { framework, language };
    console.log('the urlId', urlId);

    // First try to get existing chat
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existingChat = getRequest.result;
      const timestamp = new Date().toISOString();

      //  console.log('existingChat >>>', existingChat);

      // Prepare the chat data
      const chatData = existingChat
        ? {
            ...existingChat,
            messages,
            id,
            urlId,
            fileActions,
            description: description || existingChat.description,
            timestamp,
            techStack,
          }
        : {
            id,
            messages,
            urlId,
            description,
            fileActions,
            timestamp,
            techStack,
          };

      // Put the new or updated chat data
      const putRequest = store.put(chatData);

      putRequest.onsuccess = () => {
        //  console.log(`[setMessages] Success ${existingChat ? 'updating' : 'creating'} chat:`, chatData);
        resolve();
      };

      putRequest.onerror = (error) => {
        // console.error(`[setMessages] Error ${existingChat ? 'updating' : 'creating'} chat:`, error);
        reject(putRequest.error);
      };
    };

    getRequest.onerror = (error) => {
      // console.error('[setMessages] Error checking for existing chat:', error);
      reject(getRequest.error);
    };
  });
}

export async function setFileActions(
  db: IDBDatabase,
  id: string,
  fileActions: StoreFileAction[],
  urlId?: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');

    // First try to get existing chat
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existingChat = getRequest.result;
      const timestamp = new Date().toISOString();

      // Prepare the chat data
      const chatData = {
        ...existingChat,
        id,
        urlId,
        fileActions,
        timestamp,
      };

      // Put the new or updated chat data
      const putRequest = store.put(chatData);

      putRequest.onsuccess = () => {
        resolve();
      };

      putRequest.onerror = (error) => {
        reject(putRequest.error);
      };
    };

    getRequest.onerror = (error) => {
      reject(getRequest.error);
    };
  });
}

export async function getFileActions(db: IDBDatabase, id: string): Promise<StoreFileAction[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result.fileActions);
    request.onerror = () => reject(request.error);
  });
}

export async function setMigratedMessages(
  db: IDBDatabase,
  id: string,
  messages: Message[],
  fileActions: StoreFileAction[],
  timestamp: string,
  urlId?: string,
  description?: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');

    console.log('[refresh] files from db: ', fileActions);

    const request = store.put({
      id,
      messages,
      urlId,
      description,
      fileActions,
      timestamp,
    });

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getMessages(db: IDBDatabase, id: string): Promise<ChatHistoryItem> {
  return (await getMessagesById(db, id)) || (await getMessagesByUrlId(db, id));
}

export async function getMessagesByUrlId(db: IDBDatabase, id: string): Promise<ChatHistoryItem> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const index = store.index('urlId');
    const request = index.get(id);

    request.onsuccess = () => resolve(request.result as ChatHistoryItem);
    request.onerror = () => reject(request.error);
  });
}

export async function getMessagesById(db: IDBDatabase, id: string): Promise<ChatHistoryItem> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result as ChatHistoryItem);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteById(db: IDBDatabase, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.delete(id);

    request.onsuccess = () => resolve(undefined);
    request.onerror = () => reject(request.error);
  });
}

export async function getNextId(db: IDBDatabase): Promise<string> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.getAllKeys();

    request.onsuccess = () => {
      const highestId = request.result.reduce((cur, acc) => Math.max(+cur, +acc), 0);
      resolve(String(+highestId + 1));
    };

    request.onerror = () => reject(request.error);
  });
}

export async function getUrlId(db: IDBDatabase, id: string): Promise<string> {
  console.log('getUrlId called with id:', id);

  const idList = await getUrlIds(db);
  console.log('Retrieved idList:', idList);

  if (!idList.includes(id)) {
    console.log('ID not in idList, returning:', id);
    return id;
  } else {
    let i = 2;
    console.log('ID found in idList, starting increment loop');

    while (idList.includes(`${id}-${i}`)) {
      console.log(`ID ${id}-${i} already exists, incrementing i`);
      i++;
    }

    const newId = `${id}-${i}`;
    console.log('Returning new ID:', newId);
    return newId;
  }
}

async function getUrlIds(db: IDBDatabase): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const idList: string[] = [];

    const request = store.openCursor();

    request.onsuccess = (event: Event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      console.log('cursor >>>', cursor, cursor?.value);

      if (cursor) {
        idList.push(cursor.value.urlId);
        cursor.continue();
      } else {
        resolve(idList);
      }
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

export const updateNetlifyUrl = async (
  db: IDBDatabase,
  chatId: string,
  deployData: {
    netlifyUrl: string;
    netlifySiteId: string;
    netlifyToken: string;
    inspectDeployment: string;
  },
) => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;

      if (chat) {
        chat.netlifyCreds = deployData;

        const updateRequest = store.put(chat);
        updateRequest.onsuccess = () => resolve(chat);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Chat not found'));
      }
    };

    request.onerror = () => reject(request.error);
  });
};

export const updateProjectRules = async (db: IDBDatabase, chatId: string, rules: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;

      if (chat) {
        // Update or set the projectRules field
        chat.projectRules = rules;

        const updateRequest = store.put(chat);
        updateRequest.onsuccess = () => resolve(true);

        updateRequest.onerror = () => {
          console.error('[db] Error updating rules:', updateRequest.error);
          resolve(false);
        };
      } else {
        console.warn('[db] Chat not found when updating rules');
        resolve(false);
      }
    };

    request.onerror = () => {
      console.error('[db] Error getting chat for rules update:', request.error);
      resolve(false);
    };
  });
};

export const getRules = async (db: IDBDatabase, chatId: string): Promise<string | null> => {
  return new Promise((resolve) => {
    const transaction = db.transaction(['chats'], 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;
      resolve(chat?.projectRules || null);
    };

    request.onerror = () => {
      console.error('[db] Error loading rules:', request.error);
      resolve(null); // Resolve with null instead of rejecting
    };
  });
};

export async function getFramework(
  db: IDBDatabase,
  chatId: string,
): Promise<{ framework: string; language: string } | undefined> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('chats', 'readonly');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      console.log('request >>>', request.result);

      const chat = request.result;
      resolve(chat?.techStack);
    };

    request.onerror = () => reject(request.error);
  });
}

export const updateFramework = async (db: IDBDatabase, chatId: string, framework: string, language: string) => {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['chats'], 'readwrite');
    const store = transaction.objectStore('chats');
    const request = store.get(chatId);

    request.onsuccess = () => {
      const chat = request.result;

      if (chat) {
        chat.techStack = { language, framework };

        const updateRequest = store.put(chat);
        updateRequest.onsuccess = () => resolve(chat);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Chat not found'));
      }
    };

    request.onerror = () => reject(request.error);
  });
};
